import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('empathiq_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('empathiq_token');
            localStorage.removeItem('empathiq_user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    me: () => api.get('/auth/me'),
};

// Employee APIs
export const employeeAPI = {
    getAll: () => api.get('/employees'),
    getById: (id) => api.get(`/employees/${id}`),
    getMyProfile: () => api.get('/employees/me/profile'),
    create: (data) => api.post('/employees/create', data),
    update: (id, data) => api.put(`/employees/${id}`, data),
    delete: (id) => api.delete(`/employees/${id}`),
};

// Wellbeing APIs
export const wellbeingAPI = {
    logMood: (data) => api.post('/wellbeing/mood', data),
    getMoodHistory: (limit = 30) => api.get(`/wellbeing/mood/history?limit=${limit}`),
    getRecommendations: () => api.get('/wellbeing/recommendations'),
};

// Analytics APIs
export const analyticsAPI = {
    analyzeSentiment: (data) => api.post('/analytics/sentiment', data),
    getPsychometricProfile: () => api.get('/analytics/psychometric'),
};

// Dashboard APIs
export const dashboardAPI = {
    getMetrics: () => api.get('/dashboard/metrics'),
    getHeatmap: () => api.get('/dashboard/heatmap'),
};

// Alerts APIs
export const alertsAPI = {
    getAll: () => api.get('/alerts'),
    generate: () => api.post('/alerts/generate'),
    dismiss: (alertId) => api.put(`/alerts/${alertId}/dismiss`),
    delete: (alertId) => api.delete(`/alerts/${alertId}`),
};

// Surveys APIs
export const surveysAPI = {
    getAll: () => api.get('/surveys'),
    create: (data) => api.post('/surveys', data),
    respond: (surveyId, responses) => api.post(`/surveys/${surveyId}/respond`, responses),
};

// Voice Analysis APIs
export const voiceAPI = {
    analyze: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/voice/analyze', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getHistory: () => api.get('/voice/history'),
};

// Platform Owner APIs
export const platformAPI = {
    getStats: () => api.get('/platform/stats'),
    getOrganizations: () => api.get('/platform/organizations'),
    createOrganization: (data) => api.post('/platform/organizations', data),
    updateOrganization: (id, data) => api.put(`/platform/organizations/${id}`, data),
    deleteOrganization: (id) => api.delete(`/platform/organizations/${id}`),
    getUsers: () => api.get('/platform/users'),
    getOrganizationUsers: (orgId) => api.get(`/platform/organizations/${orgId}/users`),
    updateUserRole: (userId, role) => api.put(`/platform/users/${userId}/role`, { role }),
    getAnalytics: () => api.get('/platform/analytics'),
    clearAllData: () => api.delete('/platform/clear-all-data'),
};

// Helper methods for components
api.getPlatformStats = () => api.get('/platform/stats');
api.getPlatformOrganizations = () => api.get('/platform/organizations');
api.updateOrganization = (id, data) => api.put(`/platform/organizations/${id}`, data);
api.getOrganizationUsers = (orgId) => api.get(`/platform/organizations/${orgId}/users`);
api.updateUserRole = (userId, role) => api.put(`/platform/users/${userId}/role`, { role });

export default api;
